import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuthContext } from "@charphq/ui/providers/auth-provider";
import { ProfileUpdateForm } from "@charphq/ui/components/profile/ProfileUpdateForm";
import { PasswordChangeForm } from "@charphq/ui/components/profile/PasswordChangeForm";
import { Button } from "@charphq/ui/components/base/button";
import { useEffect } from "react";
import { authClient } from "../lib/auth-client";
import { patchSessionUser } from "../lib/auth-utils";

export const Route = createFileRoute("/profile")({
  component: function Profile() {
    const { isAuthenticated, user, signOut, isLoading, refetch } = useAuthContext();
    const navigate = useNavigate();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate({ to: "/auth" });
      }
    }, [isAuthenticated, isLoading, navigate]);

    if (isLoading) {
      return (
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return null;
    }

    return (
      <div className="mx-auto max-w-2xl">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground">Profile</h1>
              <p className="text-muted-foreground">Manage your account and security</p>
            </div>
            {signOut && (
              <Button variant="outline" onClick={() => signOut()}>
                Sign Out
              </Button>
            )}
          </div>

          <ProfileUpdateForm
            defaultName={user?.name ?? ""}
            email={user?.email ?? ""}
            onSubmit={async ({ name }) => {
              const res = await authClient.updateUser({ name });
              if (res?.error) throw res.error;
              // Reflect the new name in the cached session immediately so it
              // doesn't flash the old value while the refetch below round-trips.
              patchSessionUser({ name });
              refetch();
            }}
          />

          <PasswordChangeForm
            onSubmit={async ({ currentPassword, newPassword }) => {
              const res = await authClient.changePassword({
                currentPassword,
                newPassword,
                revokeOtherSessions: true,
              });
              if (res?.error) throw res.error;
            }}
          />
        </div>
      </div>
    );
  },
});
